/**
 * Payroll calculation engine — EXACT port of v2.9 calculateCommission_,
 * buildContext_, matchSplitRule_, matchFeeOverride_, routePayees_ and
 * buildCdCounts_ (docs/Payroll/PayrollAutomation_v2.gs).
 *
 * ONE ENGINE, ONE CODE PATH: batch runs, backtests, and the Calculate This
 * Comp calculator all call evaluateRow()/calculateCommission(). Pure module —
 * no I/O, no Node builtins — shared verbatim with the frontend bundle.
 *
 * Inputs
 *   rawRow    — named raw cell values (see functions/Payroll/sheets/pipelineReader.js
 *               buildRawRow, or the calculator's synthesized equivalent).
 *   snapshot  — reference snapshot (see functions/Payroll/reference/snapshotLoader.js):
 *               { profiles, margin, marginAliases, loaFees, splitRules,
 *                 referralRules, emailAliases, config }.
 *   monthCounts — { "email|Month": count } from buildCdCounts().
 *   options   — calculator-only overrides; batch runs pass nothing:
 *               { forceCalc, pricingOverride, feeOverride: {charged, amount},
 *                 submissionAdder: 'none'|'2nd'|'3rd' }
 *
 * Output result (null when the row is skipped — flags on ctx say why):
 *   { primary, secondary, formula, compCodeAppend, tierApplied, thirdParty[],
 *     steps[], resolved: {...structured facts for RunRow/calculator} }
 */

const { toNum, round2, fmt, canonicalEmail } = require('./numeric');
const { TAGS, resolveDealSource } = require('./tags');
const { shareOf } = require('./shareOf');
const { resolvePricingContext } = require('./pricingContext');

// -------------------------------------------------------------------------
// Context (port of buildContext_)
// -------------------------------------------------------------------------
function buildContext(rawRow, snapshot) {
  const aliases = snapshot.emailAliases || {};
  const compCode = String(rawRow.compCode || '');
  const primaryEmail = canonicalEmail(rawRow.primaryLo, aliases);
  const secondaryEmail = canonicalEmail(rawRow.secondaryLo, aliases);
  const flags = [];

  const prof = primaryEmail ? snapshot.profiles[primaryEmail] : null;
  const sProf = secondaryEmail ? snapshot.profiles[secondaryEmail] : null;
  if (primaryEmail && !prof) flags.push('UNMATCHED_LO:' + primaryEmail);
  if (secondaryEmail && !sProf) flags.push('UNMATCHED_LO:' + secondaryEmail);
  if (prof && prof.status === 'VERIFY') flags.push('PROFILE_INCOMPLETE:' + primaryEmail);
  if (sProf && sProf.status === 'VERIFY') flags.push('PROFILE_INCOMPLETE:' + secondaryEmail);

  return {
    rowNum: rawRow.rowNum,
    flags,
    compCode,
    client: rawRow.client,
    loan: toNum(rawRow.loanAmount),
    revenue: toNum(rawRow.revenue),
    lender: String(rawRow.lender || '').trim().toLowerCase(),
    product: String(rawRow.product || ''),
    processor: String(rawRow.processor || '').trim(),
    month: String(rawRow.month || '').trim(),
    closingDate: rawRow.closingDate || null,
    corrCol: String(rawRow.correspondentCol || '').trim().toLowerCase(),
    investor: String(rawRow.investor || '').trim().toLowerCase(),
    minNumber: rawRow.minNumber,
    dealSourceCol: String(rawRow.dealSourceCol || ''),
    primaryEmail,
    secondaryEmail,
    prof: prof || null,
    sProf: sProf || null,
    primaryCommissionCell: rawRow.primaryCommissionCell,
    manualSecondaryComm: toNum(rawRow.secondaryCommissionCell),
  };
}

// -------------------------------------------------------------------------
// Split-rule matcher (port of matchSplitRule_): priority ascending,
// first match wins, effective-dated.
// -------------------------------------------------------------------------
function matchSplitRule(ctx, snapshot) {
  const cc = ctx.compCode.toUpperCase();
  for (const rule of snapshot.splitRules) { // pre-sorted by priority
    if (!rule.active) continue;
    if (rule.effective && ctx.closingDate && ctx.closingDate < rule.effective) continue;
    if (rule.primary && rule.secondary) {
      const direct = ctx.primaryEmail === rule.primary && ctx.secondaryEmail === rule.secondary;
      const flipped = rule.eitherOrder && ctx.primaryEmail === rule.secondary && ctx.secondaryEmail === rule.primary;
      if (!direct && !flipped) continue;
    } else if (rule.primary && !rule.secondary) {
      if (ctx.primaryEmail !== rule.primary) continue;
    }
    if (rule.compCodeContains && !cc.includes(rule.compCodeContains)) continue;
    if (rule.condProductContains && !ctx.product.toUpperCase().includes(rule.condProductContains)) continue;
    if (rule.condLPBP === 'LP' && (ctx._useBP || ctx._isCorr)) continue;
    if (rule.condLPBP === 'BP_OR_CORR' && !(ctx._useBP || ctx._isCorr)) continue;
    return rule;
  }
  return null;
}

// -------------------------------------------------------------------------
// Fee-override matcher (port of matchFeeOverride_) — array order, not priority.
// -------------------------------------------------------------------------
function matchFeeOverride(ctx, snapshot) {
  for (const rule of snapshot.referralRules) {
    if (rule.type === 'FEE_OVERRIDE' && rule.active &&
        rule.compCodeContains && ctx.compCode.toUpperCase().includes(rule.compCodeContains)) {
      return rule.amount;
    }
  }
  return null;
}

// -------------------------------------------------------------------------
// Main calculation (port of the wrapped calculateCommission_)
// -------------------------------------------------------------------------
function calculateCommission(ctx, snapshot, monthCounts, options = {}) {
  const config = snapshot.config || {};
  // Pre-compute Corr status, margin source, and LP/BP once so the split-rule
  // matcher and the main calc share a single determination (v2.9 wrapper).
  resolvePricingContext(ctx, snapshot, options);

  const f = []; // formula narrative pieces
  const steps = [];
  const step = (id, label, detail, applied = true) => steps.push({ n: steps.length + 1, id, label, detail, applied });
  const flags = ctx.flags;
  const prof = ctx.prof;

  step('correspondent', 'Correspondent determination',
    ctx._isCorr
      ? `Correspondent (${ctx.corrCol === 'correspondent' ? 'Correspondent column' : ctx.lender === 'future home loans' ? 'Lender is Future Home Loans' : 'comp-code tag / Min Number'})${ctx.investor ? `; margin row from Investor "${ctx.investor}"` : ''}`
      : 'Wholesale (not correspondent)');
  step('margin', 'Margin resolution',
    ctx._margin
      ? `Row "${ctx._marginRowName}", column ${ctx._marginColLabel} → ${isNaN(ctx._marginPctNum) ? 'BP / no numeric margin' : `${ctx._marginPctNum}%`}${ctx._margin.maxComp > 0 ? `, Max Comp $${fmt(ctx._margin.maxComp)}` : ''}`
      : `No margin row for "${ctx._marginRowName}"`);
  step('lpbp', 'LP vs BP',
    ctx._useBP
      ? `BP — ${ctx._bpReason}${ctx._pricingOverridden ? ' (manual override)' : ''}`
      : `LP${ctx._pricingOverridden ? ' (manual override)' : ''}${ctx._bpTag && !ctx._useBP ? (isNaN(ctx._marginPctNum) ? ' — BP tag ignored (no margin data to verify; D-50)' : ' — BP tag overridden by margin') : ''}`);

  // ---- House account: no primary comp; secondary carries the payout ----
  if (prof && prof.isHouse) {
    flags.push('HOUSE_DEAL');
    if (!(ctx.manualSecondaryComm > 0)) flags.push('HOUSE_SECONDARY_EMPTY');
    step('house', 'House account', 'Primary $0; pay via Secondary.');
    return {
      primary: 0, secondary: null,
      formula: 'House account — primary $0; pay via Secondary.',
      compCodeAppend: null, tierApplied: 0, thirdParty: [], steps,
      resolved: resolvedFacts(ctx, { pricing: 'HOUSE', rateSet: null, dealSource: null, dealSourceHow: null, tierApplied: 0 }),
    };
  }
  if (!prof) { flags.push('SKIPPED_NO_PROFILE'); return null; }
  if (isNaN(ctx.loan) || isNaN(ctx.revenue) || !ctx.lender) {
    flags.push('MISSING_CORE_DATA'); return null;
  }

  // ---- Deal source ------------------------------------------------------
  const ds = resolveDealSource(ctx.dealSourceCol, ctx.compCode, prof);
  if (ds.how === 'assumed') flags.push('SOURCE_ASSUMED_COMPANY');
  if (ds.how === 'conflict') flags.push('SOURCE_TAG_CONFLICT');
  step('source', 'Deal source', `${ds.source} (via ${ds.how})`);

  // ---- Split rule lookup (may override the rate row, e.g. 2 Navy Vets) --
  const splitRule = matchSplitRule(ctx, snapshot);
  let rateProf = prof;
  if (splitRule && splitRule.rateRowOverride) {
    rateProf = snapshot.profiles[splitRule.rateRowOverride] || prof;
    f.push(`Rates from ${splitRule.rateRowOverride}`);
  }
  step('split-match', 'Split rule', splitRule
    ? `Matched ${splitRule.id} (priority ${splitRule.priority})${splitRule.rateRowOverride ? `, rates from ${splitRule.rateRowOverride}` : ''}`
    : 'No split rule matched', !!splitRule);

  // ---- LP vs BP vs Correspondent (resolved in the pricing context) -------
  const isCorr = ctx._isCorr;
  const isHELOC = TAGS.HELOC.test(ctx.product) || TAGS.HELOC.test(ctx.compCode);
  const margin = ctx._margin; // {jax, davidson, maxComp} or undefined
  if (!margin) flags.push('LENDER_NOT_IN_MARGIN_TAB:' + ctx._marginName);
  const useBP = ctx._useBP;
  const bpReason = ctx._bpReason;

  // ---- Pick the rate set -------------------------------------------------
  const src = ds.source;
  let lpPct = null, bpPct = null, fee = 0, flatAmount = null, tierApplied = 0;
  let rateSet;

  if (src === 'BROKER_REF' && rateProf.brokerRefFlat) {
    flatAmount = rateProf.brokerRefFlat;
    flags.push('BROKER_REF_FLAT');
    f.push(`Broker-referral flat $${flatAmount}`);
    rateSet = 'BROKER_REF_FLAT';
  } else if (src === 'COMPANY') {
    if (rateProf.cdTier1) { // DTC plan with tiers
      const key = ctx.primaryEmail + '|' + ctx.month;
      const count = (monthCounts && monthCounts[key]) || 1;
      const tier2 = count >= rateProf.cdTrigger && rateProf.cdTier2;
      tierApplied = tier2 ? 2 : 1;
      lpPct = (tier2 ? rateProf.cdTier2 : rateProf.cdTier1) / 100;
      bpPct = rateProf.cdBP / 100;
      fee = rateProf.cdFee || 0;
      f.push(`CD-LP Tier${tierApplied} ${(lpPct * 100).toFixed(2)}% (month count ${count}/${rateProf.cdTrigger})`);
      rateSet = 'CD_TIERED';
    } else if (rateProf.cdFlat) { // flat-per-closing company deals
      const key = ctx.primaryEmail + '|' + ctx.month;
      const count = (monthCounts && monthCounts[key]) || 1;
      if (rateProf.cdFlatTier2 && count >= rateProf.cdTrigger) {
        tierApplied = 2; flatAmount = rateProf.cdFlatTier2;
        f.push(`CD-Flat Tier2 $${flatAmount}/closing (month count ${count} ≥ ${rateProf.cdTrigger})`);
      } else if (rateProf.cdFlatTier2) {
        tierApplied = 1; flatAmount = rateProf.cdFlat;
        f.push(`CD-Flat Tier1 $${flatAmount}/closing (month count ${count}/${rateProf.cdTrigger})`);
      } else {
        flatAmount = rateProf.cdFlat;
        f.push(`Company-deal flat $${flatAmount} (outside LO)`);
      }
      flags.push('CD_FLAT_APPLIED');
      rateSet = 'CD_FLAT';
    } else {
      flags.push('CD_RATES_MISSING'); // company deal, no CD config
      lpPct = rateProf.ssLP / 100; bpPct = rateProf.ssBP / 100; fee = rateProf.ssFee || 0;
      rateSet = 'CD_MISSING_SS_FALLBACK';
    }
  } else { // SELF-sourced
    lpPct = rateProf.ssLP / 100; bpPct = rateProf.ssBP / 100; fee = rateProf.ssFee || 0;
    rateSet = 'SS';
  }
  step('rate-set', 'Rate set', flatAmount !== null
    ? `${rateSet}: flat $${flatAmount}${tierApplied ? ` (tier ${tierApplied})` : ''}`
    : `${rateSet}: LP ${lpPct !== null ? (lpPct * 100).toFixed(4) + '%' : '—'} / BP ${bpPct !== null ? (bpPct * 100).toFixed(2) + '%' : '—'}, fee $${fee}${tierApplied ? ` (tier ${tierApplied})` : ''}`);

  // ---- Fee overrides ------------------------------------------------------
  let feeNote = '';
  const feeOverride = matchFeeOverride(ctx, snapshot);
  if (feeOverride !== null) { fee = feeOverride; feeNote = '(fee override)'; }
  else if (isCorr && rateProf.feeWaivedCorr) { fee = 0; feeNote = '(fee waived: correspondent)'; }
  else if (isHELOC && !ctx.processor && rateProf.feeWaivedHelocNoProc) { fee = 0; feeNote = '(fee waived: HELOC no processor)'; }
  let feeManuallyOverridden = false;
  if (options.feeOverride && options.feeOverride.charged === false) {
    if (fee !== 0) feeManuallyOverridden = true;
    fee = 0;
  } else if (options.feeOverride && options.feeOverride.charged === true &&
             options.feeOverride.amount !== undefined && options.feeOverride.amount !== null) {
    if (fee !== options.feeOverride.amount) feeManuallyOverridden = true;
    fee = options.feeOverride.amount;
  }

  // ---- D-51: 90-day new-hire TCPI grace (leadership ruling 2026-11-03) ------
  // From the LO's company start_date through day 90 (inclusive, start = day 0),
  // the self-sourced single-submission fee is capped at $200 — unless the
  // contract fee is already lower. Explicit overrides (fee_overrides table,
  // calculator manual) and the $0 waivers above take precedence and are never
  // raised. Company-deal fees (cdFee / flat) and the 2nd/3rd submission adders
  // below are untouched. No start_date on the profile = no grace window.
  let graceNote = '';
  if (feeOverride === null && !options.feeOverride && fee > 200 &&
      (rateSet === 'SS' || rateSet === 'CD_MISSING_SS_FALLBACK') &&
      prof.startDate && ctx.closingDate) {
    const startMs = Date.parse(String(prof.startDate).slice(0, 10) + 'T00:00:00Z');
    const closeMs = Date.UTC(ctx.closingDate.getUTCFullYear(), ctx.closingDate.getUTCMonth(), ctx.closingDate.getUTCDate());
    const dayN = Math.floor((closeMs - startMs) / 86400000);
    if (!isNaN(dayN) && dayN >= 0 && dayN <= 90) {
      const feeWas = fee;
      fee = 200;
      graceNote = ` (90-day new-hire grace: was $${feeWas}, day ${dayN})`;
      flags.push('TCPI_90D_GRACE');
      step('fee-grace', 'New-hire TCPI grace',
        `Closing is day ${dayN} of 90 from start ${String(prof.startDate).slice(0, 10)} — fee capped at $200 (contract $${feeWas})`);
    }
  }

  step('fee-resolve', 'Processing fee resolution',
    `$${fee}${feeNote ? ' ' + feeNote : ''}${graceNote}${feeManuallyOverridden ? ' (manual override)' : ''}`);

  // ---- Base commission -----------------------------------------------------
  let comp;
  let pricing;
  let maxCompApplied = false;
  const maxCompSlack = config.max_comp_slack === undefined ? 1 : config.max_comp_slack;
  if (flatAmount !== null) {
    comp = flatAmount;
    pricing = 'FLAT';
    step('base', 'Base commission', `Flat $${flatAmount}`);
  } else if (isHELOC && rateProf.helocPct) { // DTC HELOC override (30%)
    comp = ctx.revenue * (rateProf.helocPct / 100);
    f.push(`HELOC ${rateProf.helocPct}% × revenue ${ctx.revenue}`);
    pricing = 'HELOC_OVERRIDE';
    step('base', 'Base commission', `HELOC override ${rateProf.helocPct}% × revenue ${ctx.revenue} = ${fmt(comp)}`);
  } else if (!useBP) {
    // LENDER-PAID — with Max Comp enforcement (D-11)
    const jaxPct = !isNaN(ctx._marginPctNum) ? ctx._marginPctNum / 100 : null;
    const fullComp = jaxPct !== null ? ctx.loan * jaxPct : null;
    if (jaxPct !== null && margin.maxComp > 0 && fullComp > margin.maxComp + maxCompSlack) {
      const fb = (rateProf.maxCompFallback || 67) / 100;
      comp = ctx.revenue * fb;
      f.push(`MAX COMP $${fmt(margin.maxComp)} reached — full comp would be $${fmt(fullComp)} (${ctx.loan} × ${(jaxPct * 100).toFixed(3)}%); paid ${(fb * 100)}% of total commission = ${ctx.revenue} × ${fb}`);
      flags.push('MAX_COMP_APPLIED');
      maxCompApplied = true;
      step('base', 'Base commission', `Max Comp $${fmt(margin.maxComp)} binds — ${(fb * 100)}% of revenue = ${fmt(comp)}`);
    } else {
      comp = ctx.loan * lpPct;
      f.push(`LP: ${ctx.loan} × ${lpPct}`);
      step('base', 'Base commission', `LP: loan ${ctx.loan} × ${lpPct} = ${fmt(comp)}`);
    }
    pricing = 'LP';
  } else {
    // BORROWER-PAID — with revenue floor
    comp = ctx.revenue * bpPct;
    f.push(`BP (${bpReason}): ${ctx.revenue} × ${bpPct}`);
    step('base', 'Base commission', `BP (${bpReason}): revenue ${ctx.revenue} × ${bpPct} = ${fmt(comp)}`);
    const floor = rateProf.bpFloor || 0;
    const floorExempt = isHELOC && !ctx.processor; // ops support not utilized
    if (floor > 0 && !floorExempt) {
      const capped = Math.max(0, ctx.revenue - floor);
      if (capped < comp) {
        comp = capped;
        f.push(`$${floor} revenue floor binds → ${fmt(comp)}`);
        flags.push('BP_FLOOR_APPLIED');
        step('floor', 'BP revenue floor', `$${floor} floor binds → ${fmt(comp)}`);
      } else {
        step('floor', 'BP revenue floor', `$${floor} floor does not bind`, false);
      }
    }
    pricing = 'BP';
  }

  // ---- Processing fee -------------------------------------------------------
  if (fee > 0) { comp -= fee; f.push(`− ${fee} fee${graceNote}`); step('fee', 'Processing fee', `− $${fee} → ${fmt(comp)}`); }
  else if (feeNote) { f.push(feeNote); step('fee', 'Processing fee', `$0 ${feeNote}`, false); }

  // ---- Lender-submission adders ----------------------------------------------
  const adder = options.submissionAdder !== undefined
    ? options.submissionAdder
    : (TAGS.SUB3.test(ctx.compCode) ? '3rd' : TAGS.SUB2.test(ctx.compCode) ? '2nd' : 'none');
  if (adder === '3rd') { comp -= (rateProf.sub3 || 200); f.push(`− ${rateProf.sub3 || 200} (3rd submission)`); step('adders', 'Submission adder', `− $${rateProf.sub3 || 200} (3rd submission) → ${fmt(comp)}`); }
  else if (adder === '2nd') { comp -= (rateProf.sub2 || 100); f.push(`− ${rateProf.sub2 || 100} (2nd submission)`); step('adders', 'Submission adder', `− $${rateProf.sub2 || 100} (2nd submission) → ${fmt(comp)}`); }

  // ---- Third-party deductions from Referral Rules (Ethan/Tree legacy) ----------
  const thirdParty = [];
  snapshot.referralRules.forEach((rule) => {
    if (rule.type === 'THIRD_PARTY_PAYOUT' && rule.active &&
        rule.compCodeContains && ctx.compCode.toUpperCase().includes(rule.compCodeContains)) {
      if (rule.deduct) { comp -= rule.amount; f.push(`− ${rule.amount} (${rule.id})`); }
      thirdParty.push({ email: rule.payee, amount: rule.amount, note: rule.id });
      step('third-party', 'Referral payout', `${rule.id}: $${rule.amount} to ${rule.payee}${rule.deduct ? ' (deducted from primary)' : ''}`);
    }
  });

  // ---- Secondary handling: split rule > LOA schedule > manual amount -----------
  let secondary = null;
  let compCodeAppend = null;
  let secondaryHandled = null;
  if (splitRule && splitRule.secondaryShare && !ctx.secondaryEmail) {
    // Rule wants to pay a secondary, but the row has none — don't strand
    // money on a nonexistent payee; leave the full amount with the primary.
    flags.push('SPLIT_SECONDARY_MISSING');
    if (splitRule.flag) flags.push(splitRule.flag);
    f.push(`(split rule ${splitRule.id} NOT applied — no Secondary LO on row)`);
    step('secondary', 'Secondary', `Split rule ${splitRule.id} NOT applied — no Secondary LO on row`, false);
    secondaryHandled = 'split-not-applied';
  } else if (splitRule) {
    const total = comp;
    const pr = shareOf(splitRule.primaryShare, total, ctx, fee, config);
    let sec = shareOf(splitRule.secondaryShare, total, ctx, fee, config);
    if (splitRule.secondaryShare === 'remainder') sec = Math.max(0, total - pr);
    comp = pr; secondary = sec;
    if (sec > 0 && !ctx.secondaryEmail) flags.push('SPLIT_SECONDARY_MISSING');
    f.push(`= ${fmt(total)} then ${splitRule.id}: primary ${fmt(pr)} / secondary ${fmt(sec)}`);
    step('secondary', 'Secondary (split rule)', `${splitRule.id}: total ${fmt(total)} → primary ${fmt(pr)} / secondary ${fmt(sec)}`);
    if (splitRule.thirdPartyEmail) {
      const tp = shareOf(splitRule.thirdPartyShare, total, ctx, fee, config);
      thirdParty.push({ email: splitRule.thirdPartyEmail, amount: tp, note: splitRule.id });
      if (splitRule.appendNote) {
        compCodeAppend = splitRule.appendNote.replace('{TP}', tp.toFixed(2));
      }
      f.push(`third party ${splitRule.thirdPartyEmail} ${fmt(tp)}`);
      step('third-party', 'Split third party', `${splitRule.thirdPartyEmail} gets ${fmt(tp)}`);
    }
    if (splitRule.flag) flags.push(splitRule.flag);
    secondaryHandled = 'split';
  } else if (ctx.secondaryEmail && snapshot.loaFees[ctx.secondaryEmail]) {
    const loa = snapshot.loaFees[ctx.secondaryEmail];
    comp -= loa.deduct; secondary = loa.pay;
    f.push(`− ${loa.deduct} (LOA ${ctx.secondaryEmail} → paid ${loa.pay})`);
    step('secondary', 'Secondary (LOA schedule)', `− $${loa.deduct} from primary; LOA ${ctx.secondaryEmail} paid $${loa.pay}`);
    secondaryHandled = 'loa';
  } else if (!isNaN(ctx.manualSecondaryComm) && ctx.manualSecondaryComm > 0) {
    comp -= ctx.manualSecondaryComm;
    f.push(`− ${ctx.manualSecondaryComm} (manual secondary)`);
    step('secondary', 'Secondary (manual amount)', `− $${ctx.manualSecondaryComm} (from Secondary Commission cell)`);
    secondaryHandled = 'manual';
  } else if (ctx.secondaryEmail) {
    flags.push('SECONDARY_UNHANDLED:' + ctx.secondaryEmail);
    step('secondary', 'Secondary', `UNHANDLED: ${ctx.secondaryEmail} (no split rule, LOA row, or manual amount)`, false);
    secondaryHandled = 'unhandled';
  }

  // ---- Manual-adjustment tags → flags only (D-8) --------------------------------
  TAGS.MANUAL_PATTERNS.forEach(([re, flag]) => {
    if (re.test(ctx.compCode) && flags.indexOf(flag) === -1) flags.push(flag);
  });

  const formula = f.join(' ') + ` = ${comp.toFixed(2)}`;
  step('final', 'Final', `Primary ${fmt(round2(comp))}${secondary !== null ? ` / Secondary ${fmt(round2(secondary))}` : ''}`);
  return {
    primary: round2(comp),
    secondary: secondary === null ? null : round2(secondary),
    formula,
    compCodeAppend,
    tierApplied,
    thirdParty,
    steps,
    resolved: resolvedFacts(ctx, {
      pricing, rateSet, dealSource: ds.source, dealSourceHow: ds.how,
      tierApplied, maxCompApplied, secondaryHandled, splitRuleId: splitRule ? splitRule.id : null,
      fee, isHELOC,
    }),
  };
}

function resolvedFacts(ctx, extra) {
  return {
    isCorrespondent: ctx._isCorr,
    marginLender: ctx._marginRowName || null,
    marginColumn: ctx._marginColLabel || null,
    marginPct: isNaN(ctx._marginPctNum) ? null : ctx._marginPctNum,
    maxComp: ctx._margin ? ctx._margin.maxComp : null,
    bpReason: ctx._bpReason || null,
    useBP: !!ctx._useBP,
    ...extra,
  };
}

// -------------------------------------------------------------------------
// Row evaluation — the orchestrator/calculator entry point. Mirrors the
// v2.9 runPayroll_ manual-prefilled gate; backtests pass forceCalc.
// -------------------------------------------------------------------------
function evaluateRow(rawRow, snapshot, monthCounts, options = {}) {
  const ctx = buildContext(rawRow, snapshot);
  const manualPrefilled = !(ctx.primaryCommissionCell === '' || ctx.primaryCommissionCell === null);
  let result = null;
  if (!manualPrefilled || options.forceCalc) {
    result = calculateCommission(ctx, snapshot, monthCounts, options);
  } else {
    ctx.flags.push('MANUAL_PREFILLED');
  }
  return { ctx, manualPrefilled, result };
}

// -------------------------------------------------------------------------
// Routing (port of routePayees_) — payee rows for the Bank & LO Workbook.
// Payee flags are a SNAPSHOT at routing time (v2.9 concat copies the array);
// flags pushed later (true-ups) appear only in the pipeline write flags.
// -------------------------------------------------------------------------
function routePayees(ctx, result, snapshot, payeeRows) {
  const cc = ctx.compCode;
  const push = (client, amount, email, type, extraFlags) => {
    if (!email) return;
    payeeRows.push({
      client, amount, email, compCode: cc,
      flags: (extraFlags || []).concat(snapshot.profiles[email] ? [] : ['UNMATCHED_LO']),
      type,
      rowNum: ctx.rowNum,
    });
  };

  // Primary — value comes from the calc, or from the sheet if manual.
  const pAmt = result ? result.primary : toNum(ctx.primaryCommissionCell);
  if (ctx.primaryEmail && !(ctx.prof && ctx.prof.isHouse)) {
    push(ctx.client, isNaN(pAmt) ? '' : pAmt, ctx.primaryEmail, 'Primary', ctx.flags);
  }

  // Secondary
  const sAmt = result && result.secondary !== null ? result.secondary : ctx.manualSecondaryComm;
  if (ctx.secondaryEmail) {
    push(ctx.client, isNaN(sAmt) ? '' : sAmt, ctx.secondaryEmail, 'Secondary', []);
  }

  // Third parties from the calc (split rules / referral rules)
  if (result && result.thirdParty) {
    result.thirdParty.forEach((tp) => push(ctx.client, round2(tp.amount), tp.email, '3rd party', [tp.note]));
  }

  // Legacy comp-code notes on MANUAL rows (calc rows already emitted above):
  if (!result || !result.thirdParty || !result.thirdParty.length) {
    const m = cc.match(TAGS.FOSTER_NOTE);
    if (m && !(result && result.thirdParty && result.thirdParty.length)) {
      push(ctx.client, parseFloat(m[1]), 'foster@future.loans', '3rd party', ['FOSTER_NOTE']);
    }
    snapshot.referralRules.forEach((rule) => {
      if (rule.type === 'THIRD_PARTY_PAYOUT' && rule.active && !result &&
          rule.compCodeContains && cc.toUpperCase().includes(rule.compCodeContains)) {
        push(ctx.client, rule.amount, rule.payee, '3rd party', [rule.id]);
      }
    });
  }
}

// -------------------------------------------------------------------------
// Month-to-date company-deal counts (port of buildCdCounts_) — counts EVERY
// pipeline row (paid or not) whose primary LO has a tiered plan and whose
// resolved deal source is COMPANY. Recomputed every run.
// -------------------------------------------------------------------------
function buildCdCounts(rawRows, snapshot) {
  const cdCounts = {}; // key "email|Month" → count
  const aliases = snapshot.emailAliases || {};
  for (let r = 0; r < rawRows.length; r++) {
    const row = rawRows[r];
    const email = canonicalEmail(row.primaryLo, aliases);
    if (!email) continue;
    const prof = snapshot.profiles[email];
    const tiered = prof && (prof.cdTier1 || (prof.cdFlat && prof.cdFlatTier2));
    if (!tiered) continue; // month counting only needed for tiered CD plans
    const src = resolveDealSource(row.dealSourceCol, String(row.compCode || ''), prof).source;
    if (src === 'COMPANY') {
      const key = email + '|' + String(row.month || '').trim();
      cdCounts[key] = (cdCounts[key] || 0) + 1;
    }
  }
  return cdCounts;
}

module.exports = {
  buildContext,
  matchSplitRule,
  matchFeeOverride,
  calculateCommission,
  evaluateRow,
  routePayees,
  buildCdCounts,
};
